<template>
  <div class="th-page">
    <div class="th-header">
      <h4 class="th-title">Transfer History</h4>
      <div class="th-actions">
        <select v-model="filterChain" class="form-control form-control-sm" style="width:120px">
          <option value="">All chains</option>
          <option value="tron">Tron</option>
          <option value="ethereum">Ethereum</option>
          <option value="bsc">BSC</option>
          <option value="polygon">Polygon</option>
        </select>
        <select v-model="filterStatus" class="form-control form-control-sm" style="width:130px">
          <option value="">All statuses</option>
          <option value="confirmed">Confirmed</option>
          <option value="pending">Pending</option>
          <option value="failed">Failed</option>
        </select>
        <input
          v-model="searchText"
          class="form-control form-control-sm"
          placeholder="Search address or txid…"
          style="width:240px"
        />
        <button class="btn btn-sm btn-secondary" @click="load">↺ Refresh</button>
        <button class="btn btn-sm btn-outline-primary" @click="exportCsv">⬇ CSV</button>
      </div>
    </div>

    <PageLoader v-if="loading" message="Loading transfer history…" />
    <div v-else-if="!rows.length" class="th-empty">No transfers found.</div>
    <div v-else class="th-table-wrap">
      <table class="table table-sm table-hover">
        <thead>
          <tr>
            <th>Date</th>
            <th>Chain</th>
            <th>From</th>
            <th>To</th>
            <th>Amount</th>
            <th>Status</th>
            <th>TxID</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="r in rows" :key="r.id">
            <td class="td-date">{{ fmtDate(r.created) }}</td>
            <td><span class="chain-pill" :style="`color:${chainColor(r.chain)}`">{{ r.chain }}</span></td>
            <td class="td-addr" :title="r.from_address">{{ short(r.from_address) }}</td>
            <td class="td-addr" :title="r.to_address">{{ short(r.to_address) }}</td>
            <td class="td-amt">{{ fmtAmt(r.amount) }} USDT</td>
            <td><span class="status-badge" :class="`s-${r.status}`">{{ r.status }}</span></td>
            <td>
              <a v-if="r.txid" :href="explorerUrl(r)" target="_blank" class="tx-link" :title="r.txid">
                {{ r.txid.slice(0, 10) }}…
              </a>
              <span v-else class="text-muted">—</span>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <div v-if="totalPages > 1" class="th-pager">
      <button class="btn btn-sm btn-secondary" :disabled="page <= 1" @click="page--; load()">‹ Prev</button>
      <span class="pager-info">{{ page }} / {{ totalPages }}</span>
      <button class="btn btn-sm btn-secondary" :disabled="page >= totalPages" @click="page++; load()">Next ›</button>
    </div>
  </div>
</template>

<script>
import { getTransfers } from '../services/pbService';
import PageLoader from '../components/PageLoader.vue';

const CHAIN_COLOR = { tron: '#FF0013', ethereum: '#627EEA', bsc: '#F0B90B', polygon: '#8247E5' };
const EXPLORER = {
  tron:     tx => `https://tronscan.org/#/transaction/${tx}`,
  ethereum: tx => `https://etherscan.io/tx/${tx}`,
  bsc:      tx => `https://bscscan.com/tx/${tx}`,
  polygon:  tx => `https://polygonscan.com/tx/${tx}`,
};

export default {
  name: 'TransferHistory',
  components: { PageLoader },
  data() {
    return {
      allRows: [],
      loading: false,
      filterChain: '',
      filterStatus: '',
      searchText: '',
      page: 1,
      perPage: 50,
      totalPages: 1,
    };
  },
  computed: {
    rows() {
      const q = this.searchText.toLowerCase();
      return this.allRows.filter(r => {
        if (this.filterChain && r.chain !== this.filterChain) return false;
        if (this.filterStatus && r.status !== this.filterStatus) return false;
        if (q && !r.from_address?.toLowerCase().includes(q) &&
                 !r.to_address?.toLowerCase().includes(q) &&
                 !r.txid?.toLowerCase().includes(q)) return false;
        return true;
      });
    },
  },
  watch: {
    filterChain() { this.page = 1; this.load(); },
    filterStatus() { this.page = 1; this.load(); },
  },
  mounted() {
    this.load();
  },
  methods: {
    async load() {
      this.loading = true;
      try {
        const parts = [];
        if (this.filterChain)  parts.push(`chain = "${this.filterChain}"`);
        if (this.filterStatus) parts.push(`status = "${this.filterStatus}"`);
        const filter = parts.join(' && ');
        const res = await getTransfers({ page: this.page, perPage: this.perPage, filter });
        this.allRows = res.items;
        this.totalPages = res.totalPages;
      } catch (e) {
        console.error('Failed to load transfers:', e);
      } finally {
        this.loading = false;
      }
    },
    fmtDate(iso) {
      return new Date(iso).toLocaleString([], { dateStyle: 'short', timeStyle: 'short' });
    },
    fmtAmt(n) {
      return Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 6 });
    },
    short(addr) {
      if (!addr) return '—';
      return addr.length > 16 ? addr.slice(0, 8) + '…' + addr.slice(-6) : addr;
    },
    chainColor(chain) { return CHAIN_COLOR[chain] || '#aaa'; },
    explorerUrl(r) {
      return r.txid && EXPLORER[r.chain] ? EXPLORER[r.chain](r.txid) : '#';
    },
    exportCsv() {
      const cols = ['date', 'chain', 'from', 'to', 'amount', 'status', 'txid', 'notes'];
      const rows = [cols.join(',')];
      for (const r of this.rows) {
        rows.push([
          `"${r.created}"`, r.chain, `"${r.from_address || ''}"`,
          `"${r.to_address || ''}"`, r.amount || 0, r.status,
          `"${r.txid || ''}"`, `"${(r.notes || '').replace(/"/g, '""')}"`
        ].join(','));
      }
      const blob = new Blob([rows.join('\n')], { type: 'text/csv' });
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = `transfers_${Date.now()}.csv`;
      a.click();
    },
  },
};
</script>

<style scoped>
.th-page { padding: 0; }

.th-header {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 10px;
  margin-bottom: 18px;
}

.th-title {
  font-size: 1.1rem;
  font-weight: 800;
  color: var(--text);
  letter-spacing: -0.01em;
  margin: 0;
  flex-shrink: 0;
}

.th-actions {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  margin-left: auto;
}

.th-empty {
  text-align: center;
  color: var(--text-muted);
  padding: 60px 0;
  font-size: 0.88rem;
}

.th-table-wrap {
  overflow-x: auto;
  border: 1px solid var(--border);
  border-radius: var(--radius);
}

.td-date { font-size: 0.76rem; white-space: nowrap; color: var(--text-muted); }
.td-addr { font-family: monospace; font-size: 0.8rem; }
.td-amt  { font-weight: 700; white-space: nowrap; }

.chain-pill {
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
}

.status-badge {
  font-size: 0.68rem;
  font-weight: 700;
  padding: 2px 8px;
  border-radius: 8px;
  letter-spacing: 0.05em;
  text-transform: uppercase;
}
.s-confirmed { background: rgba(34,197,94,0.15); color: #86efac; }
.s-pending   { background: rgba(245,158,11,0.15); color: #fcd34d; }
.s-failed    { background: rgba(244,63,94,0.15);  color: #fca5a5; }

.tx-link {
  font-family: monospace;
  font-size: 0.78rem;
  color: var(--accent-2);
  text-decoration: none;
}
.tx-link:hover { text-decoration: underline; }

.th-pager {
  display: flex;
  align-items: center;
  gap: 12px;
  justify-content: center;
  margin-top: 16px;
}
.pager-info {
  font-size: 0.78rem;
  color: var(--text-muted);
}
</style>
